/**
 * 画面レイアウトの表示状態。
 *
 * 左ペイン(審査Web: 検索・商品一覧・NGワードリスト・セラー情報 /
 * 商品編集Web: 検索・NGワードチェック)は、本文をじっくり読むときに畳めると広く使える。
 * 審査Webと商品編集Webでは畳みたい状況が異なるため、状態は画面ごとに別で持つ。
 *
 * 審査者ごとに好みが違い、商品を切り替えるたびにリセットされると毎回押し直すことに
 * なるため localStorage に永続化する(NGワードの表示設定と同じ方針)。
 */
import { defineStore } from 'pinia'

const STORAGE_KEY = 'salmon.layout'

export type ScreenKey = 'judge' | 'productEdit'

const DEFAULT_LEFT_WIDTH = 970 // 従来の固定値
export const LEFT_WIDTH_MIN = 320
export const LEFT_WIDTH_MAX = 1400

/**
 * 画像拡大エリア(右ゾーン)が対比ゾーン全体に占める割合(%)。
 * 【pxではなく%で持つ理由】
 * 利用者ごとに画面幅がバラバラで、pxで保存すると狭いモニタで開いたときに
 * 画面からはみ出す。割合なら画面幅が変わっても比率が保たれる。
 * 左(商品属性)は残り全部を使うので、右を広げれば左は自動で狭くなる。
 */
/**
 * 審査画面 右カラムのカード並び順。
 * 審査の進め方は人によって違う(画像から見る人、本文から見る人)ため、
 * 並びを入れ替えられるようにする。既定はVB.NET版に近い順序。
 */
export const CARD_KEYS = ['header', 'images', 'title', 'compare'] as const
export type CardKey = (typeof CARD_KEYS)[number]

export const CARD_LABELS: Record<CardKey, string> = {
  header: '親ASIN・リンク',
  images: '商品画像',
  title: '商品名',
  compare: '商品属性・カテゴリ',
}

const DEFAULT_CARD_ORDER: CardKey[] = [...CARD_KEYS]

/**
 * 検索結果一覧の列幅(px)。項目名の境目をドラッグして変えられる。
 * 商品名など、既定幅では収まりきらない項目を広げて確認できるようにする。
 *
 * productName も他列と同様にドラッグで幅を変えられるようにする。
 * 一覧全体は横スクロールを許可しているため、pxで固定しても
 * 画面からあふれた分はスクロールで見られる。
 */
export const COLUMN_KEYS = [
  'status', 'seller', 'parentAsin', 'childAsin', 'brand', 'productName',
  'color', 'size', 'createDate', 'updateDate', 'judgeDate',
] as const
export type ColumnKey = (typeof COLUMN_KEYS)[number]

const DEFAULT_COLUMN_WIDTHS: Record<ColumnKey, number> = {
  status: 34, seller: 80, parentAsin: 94, childAsin: 94, brand: 84,
  productName: 260,
  color: 54, size: 50, createDate: 65, updateDate: 65, judgeDate: 65,
}
export const COLUMN_WIDTH_MIN = 40
export const COLUMN_WIDTH_MAX = 800

const DEFAULT_PREVIEW_RATIO = 42
export const PREVIEW_RATIO_MIN = 20
export const PREVIEW_RATIO_MAX = 65

interface LayoutState {
  leftPaneVisible: Record<ScreenKey, boolean>
  /** 左ペインの幅(px)。境界をドラッグして変えられる */
  leftPaneWidth: Record<ScreenKey, number>
  /** 画像拡大エリアが対比ゾーンに占める割合(%)。商品を切り替えても維持する */
  previewRatio: number
  /** 審査画面 右カラムのカード並び順 */
  cardOrder: CardKey[]
  /** 検索結果一覧の列幅(px) */
  columnWidths: Record<ColumnKey, number>
}

interface Persisted {
  visible: Record<ScreenKey, boolean>
  width: Record<ScreenKey, number>
  previewRatio: number
  cardOrder: CardKey[]
  columnWidths: Record<ColumnKey, number>
}

/** 保存値を正規化する(未知キーを除去し、欠けているキーを補う) */
function normalizeCardOrder(saved: unknown): CardKey[] {
  const list = Array.isArray(saved) ? saved : []
  const valid = list.filter((k): k is CardKey => (CARD_KEYS as readonly string[]).includes(k))
  const missing = DEFAULT_CARD_ORDER.filter((k) => !valid.includes(k))
  return [...valid, ...missing]
}

function load(): Persisted {
  const fallback: Persisted = {
    visible: { judge: true, productEdit: true },
    width: { judge: DEFAULT_LEFT_WIDTH, productEdit: DEFAULT_LEFT_WIDTH },
    previewRatio: DEFAULT_PREVIEW_RATIO,
    cardOrder: [...DEFAULT_CARD_ORDER],
    columnWidths: { ...DEFAULT_COLUMN_WIDTHS },
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return fallback
    const parsed = JSON.parse(raw) as Partial<Persisted> & Partial<Record<ScreenKey, boolean>>
    // 旧形式({judge:true, productEdit:true})からの移行に対応する
    const visible = parsed.visible ?? {
      judge: parsed.judge ?? true,
      productEdit: parsed.productEdit ?? true,
    }
    return {
      visible: { ...fallback.visible, ...visible },
      width: { ...fallback.width, ...(parsed.width ?? {}) },
      previewRatio: parsed.previewRatio ?? fallback.previewRatio,
      // 保存済みの並びが古い(キーの増減がある)場合に備えて補正する。
      // 未知のキーは捨て、足りないキーは既定の位置に追加することで、
      // 将来カードが増えても保存値のせいで表示が欠けることがない。
      cardOrder: normalizeCardOrder(parsed.cardOrder),
      // 既定値をベースに保存値を重ねる。将来列が増えても欠けない
      columnWidths: { ...DEFAULT_COLUMN_WIDTHS, ...(parsed.columnWidths ?? {}) },
    }
  } catch {
    return fallback
  }
}

export const useLayoutStore = defineStore('layout', {
  state: (): LayoutState => {
    const p = load()
    return {
      leftPaneVisible: p.visible,
      leftPaneWidth: p.width,
      previewRatio: p.previewRatio,
      cardOrder: p.cardOrder,
      columnWidths: p.columnWidths,
    }
  },

  actions: {
    persist() {
      try {
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({
            visible: this.leftPaneVisible,
            width: this.leftPaneWidth,
            previewRatio: this.previewRatio,
            cardOrder: this.cardOrder,
            columnWidths: this.columnWidths,
          }),
        )
      } catch {
        // プライベートブラウジング等で書き込めない場合は諦める(動作には影響しない)
      }
    },
    toggleLeftPane(screen: ScreenKey) {
      this.leftPaneVisible[screen] = !this.leftPaneVisible[screen]
      this.persist()
    },
    setLeftPane(screen: ScreenKey, visible: boolean) {
      this.leftPaneVisible[screen] = visible
      this.persist()
    },
    /** 画像拡大エリアの割合を設定する。永続化はドラッグ完了時にまとめて行う */
    setPreviewRatio(ratio: number) {
      this.previewRatio = Math.min(
        PREVIEW_RATIO_MAX,
        Math.max(PREVIEW_RATIO_MIN, Math.round(ratio * 10) / 10),
      )
    },
    /** カードを1つ前/後ろへ移動する */
    moveCard(key: CardKey, delta: -1 | 1) {
      const i = this.cardOrder.indexOf(key)
      const j = i + delta
      if (i < 0 || j < 0 || j >= this.cardOrder.length) return
      const next = [...this.cardOrder]
      ;[next[i], next[j]] = [next[j], next[i]]
      this.cardOrder = next
      this.persist()
    },
    /** 一覧の列幅を設定する。永続化はドラッグ完了時にまとめて行う */
    setColumnWidth(key: ColumnKey, width: number) {
      this.columnWidths = {
        ...this.columnWidths,
        [key]: Math.min(COLUMN_WIDTH_MAX, Math.max(COLUMN_WIDTH_MIN, Math.round(width))),
      }
    },
    /** 列幅を既定に戻す */
    resetColumnWidths() {
      this.columnWidths = { ...DEFAULT_COLUMN_WIDTHS }
      this.persist()
    },

    /** 並び順を既定に戻す */
    resetCardOrder() {
      this.cardOrder = [...DEFAULT_CARD_ORDER]
      this.persist()
    },

    resetPreviewRatio() {
      this.previewRatio = DEFAULT_PREVIEW_RATIO
      this.persist()
    },

    /** 境界のドラッグ中に呼ばれる。永続化はドラッグ完了時にまとめて行う */
    setLeftWidth(screen: ScreenKey, width: number) {
      this.leftPaneWidth[screen] = Math.min(LEFT_WIDTH_MAX, Math.max(LEFT_WIDTH_MIN, Math.round(width)))
    },
  },
})
